import { useCallback, useEffect, useState } from 'react';
import { PageHeader, TableSkeleton } from '../components/common/PageHeader';
import {
  getContactSettings,
  updateContactSettings,
  listContactInquiries,
  updateInquiryStatus,
} from '../services/siteService';
import { getErrorMessage } from '../services/apiClient';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/format';

const emptySettings = {
  address: '',
  phone: '',
  email: '',
  supportEmail: '',
  workingHours: '',
  heroTitle: '',
  heroSubtitle: '',
};

export default function ContactPage() {
  const { showToast } = useToast();
  const [tab, setTab] = useState('settings');
  const [settings, setSettings] = useState(emptySettings);
  const [inquiries, setInquiries] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingInquiries, setLoadingInquiries] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const res = await getContactSettings();
      const d = res.data || {};
      setSettings({
        address: d.address || '',
        phone: d.phone || '',
        email: d.email || '',
        supportEmail: d.supportEmail || '',
        workingHours: d.workingHours || '',
        heroTitle: d.heroTitle || '',
        heroSubtitle: d.heroSubtitle || '',
      });
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoadingSettings(false);
    }
  }, [showToast]);

  const loadInquiries = useCallback(async () => {
    setLoadingInquiries(true);
    try {
      const res = await listContactInquiries({ limit: 50 });
      setInquiries(res.data || []);
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoadingInquiries(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadSettings();
    loadInquiries();
  }, [loadSettings, loadInquiries]);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateContactSettings(settings);
      showToast('Contact settings saved');
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateInquiryStatus(id, status);
      showToast('Status updated');
      loadInquiries();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    }
  };

  return (
    <div>
      <PageHeader
        title="Contact"
        description="Manage contact page info and customer messages"
      />

      <div className="tabs mb-4" style={{ display: 'flex', gap: '8px',marginBottom: '10px' }}>
        <button
          type="button"
          className={`btn ${tab === 'settings' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setTab('settings')}
        >
          Contact Info
        </button>
        <button
          type="button"
          className={`btn ${tab === 'inquiries' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setTab('inquiries')}
        >
          Messages ({inquiries.length})
        </button>
      </div>

      {tab === 'settings' && (
        <div className="card">
          {loadingSettings ? (
            <p className="p-6 text-muted">Loading...</p>
          ) : (
            <form onSubmit={handleSaveSettings} className="p-6">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Hero Title</label>
                  <input
                    className="input"
                    value={settings.heroTitle}
                    onChange={(e) => setSettings({ ...settings, heroTitle: e.target.value })}
                  />
                </div>
                <div className="form-group full">
                  <label>Hero Subtitle</label>
                  <textarea
                    className="input textarea"
                    rows={2}
                    value={settings.heroSubtitle}
                    onChange={(e) => setSettings({ ...settings, heroSubtitle: e.target.value })}
                  />
                </div>
                <div className="form-group full">
                  <label>Address</label>
                  <textarea
                    className="input textarea"
                    rows={2}
                    value={settings.address}
                    onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    className="input"
                    value={settings.phone}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    className="input"
                    value={settings.email}
                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Support Email</label>
                  <input
                    className="input"
                    value={settings.supportEmail}
                    onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
                  />
                </div>
                <div className="form-group full">
                  <label>Working Hours</label>
                  <textarea
                    className="input textarea"
                    rows={2}
                    value={settings.workingHours}
                    onChange={(e) => setSettings({ ...settings, workingHours: e.target.value })}
                  />
                </div>
              </div>
              <div className="mt-4 ">
                <button type="submit" className="btn btn-primary" disabled={saving}  style={{marginTop: '10px'}}>
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {tab === 'inquiries' && (
        <div className="card">
          {loadingInquiries ? (
            <TableSkeleton rows={5} cols={5} />
          ) : inquiries.length === 0 ? (
            <p className="p-6 text-muted">No messages yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Subject</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {inquiries.map((row) => (
                    <tr key={row._id}>
                      <td>{formatDate(row.createdAt)}</td>
                      <td>
                        {row.firstName} {row.lastName}
                      </td>
                      <td>{row.email}</td>
                      <td>
                        <details>
                          <summary className="cursor-pointer">{row.subject}</summary>
                          <p className="text-sm mt-2 max-w-md">{row.message}</p>
                          {row.phone && <p className="text-sm">Phone: {row.phone}</p>}
                        </details>
                      </td>
                      <td>
                        <select
                          className="input"
                          value={row.status}
                          onChange={(e) => handleStatusChange(row._id, e.target.value)}
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="replied">Replied</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
